import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RefreshCw, X } from 'lucide-react';

const PWAUpdateNotification: React.FC = () => {
  const { t } = useTranslation();
  const [showUpdate, setShowUpdate] = useState(false);
  const [offlineReady, setOfflineReady] = useState(false);

  useEffect(() => {
    // Register service worker and handle updates
    if ('serviceWorker' in navigator) {
      let refreshing = false;
      
      const registerSW = async () => {
        try {
          const registration = await navigator.serviceWorker.register('/sw.js', {
            scope: '/',
            updateViaCache: 'none' // Prevent aggressive caching of SW itself
          });

          console.log('🔄 Service Worker registered:', registration);

          // Listen for updates
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  console.log('🆕 New app version available');
                  setShowUpdate(true);
                }
              });
            }
          });

          // Check for updates every 60 seconds
          setInterval(async () => {
            try {
              await registration.update();
            } catch (error) {
              console.log('🔄 SW update check failed:', error);
            }
          }, 60000);

          // Listen for controlling changes
          navigator.serviceWorker.addEventListener('controllerchange', () => {
            if (refreshing) return;
            window.location.reload();
            refreshing = true;
          });

        } catch (error) {
          console.error('🚫 Service Worker registration failed:', error);
        }
      };

      registerSW();

      // Check if app is ready to work offline
      if (navigator.serviceWorker.controller) {
        setOfflineReady(true);
        setTimeout(() => setOfflineReady(false), 3000);
      }
    }
  }, []);

  const handleUpdate = async () => {
    console.log('🔄 Updating to new version...');
    setShowUpdate(false);
    
    try {
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration?.waiting) {
          // Tell the waiting service worker to skip waiting and become active
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
      }
      
      // Force a hard reload to ensure all cached resources are refreshed
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (error) {
      console.error('🚫 Update failed:', error);
      // Fallback: force hard reload
      window.location.reload();
    }
  };

  const handleDismiss = () => {
    setShowUpdate(false);
  };

  if (!showUpdate && !offlineReady) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-[9999] max-w-md mx-auto">
      {showUpdate && (
        <Alert className="bg-primary text-primary-foreground border-primary">
          <RefreshCw className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between gap-3">
            <span className="flex-1">
              {t('common.pwa.newVersionAvailable')}
            </span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={handleUpdate}
                className="h-8 px-3 text-sm bg-background text-foreground hover:bg-background/90"
              >
                {t('common.update')}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleDismiss}
                className="h-8 w-8 p-0 text-primary-foreground hover:bg-primary-foreground/20"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default PWAUpdateNotification;